/**
 * AdminGlovesPricing — internal cost + retail + margin table for AMMEX gloves.
 * Route: /admin/gloves-pricing (nested inside AdminDashboard)
 *
 * Access: role super_admin or hub_admin. The backend endpoint
 *   GET /api/gloves/cost-pricing
 * enforces the same restriction — this page will render empty rows for
 * anyone else.
 *
 * Customer-facing gloves marketplace (/marketplace/gloves) NEVER shows
 * cost figures — cost data lives on the backend and is fetched only from
 * this admin page.
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { GLOVES } from "@/data/glovesCatalog";
import { Lock, TrendingUp, Package, AlertTriangle } from "lucide-react";

const fmt = (n) => (typeof n === "number" ? `$${n.toFixed(2)}` : "—");
const fmtPiece = (n) => (typeof n === "number" ? `$${n.toFixed(4)}` : "—");

export default function AdminGlovesPricing() {
  const { token, user } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [ansellCost, setAnsellCost] = useState({});
  const [ansellProducts, setAnsellProducts] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!token) {
      setError("Please log in as a super_admin or hub_admin to view cost pricing.");
      setLoading(false);
      return;
    }
    Promise.all([
      axios.get(`${API}/gloves/cost-pricing`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/gloves/ansell/cost-pricing`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/gloves/ansell`),
    ])
      .then(([ammex, ansellCost, ansellPub]) => {
        setPricing(ammex.data);
        setAnsellCost(ansellCost.data.items || {});
        setAnsellProducts(ansellPub.data.products || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.status === 403 || err.response?.status === 401
          ? "Access denied — cost pricing is restricted to super_admin / hub_admin."
          : "Failed to load cost pricing. Try again.");
        setLoading(false);
      });
  }, [token]);

  const rows = useMemo(() => {
    if (!pricing) return [];
    const items = pricing.items || {};
    return GLOVES
      .filter((g) => filter === "all" || g.material === filter)
      .map((g) => ({
        product: g,
        cost: items[g.id] || null,
      }));
  }, [pricing, filter]);

  const totals = useMemo(() => {
    if (!pricing) return null;
    const items = Object.values(pricing.items || {});
    const totalCost = items.reduce((s, i) => s + i.cost_per_case, 0);
    const totalRetail = items.reduce((s, i) => s + i.retail_per_case, 0);
    return {
      lines: items.length,
      totalCost,
      totalRetail,
      totalMargin: totalRetail - totalCost,
      markupPct: pricing.markup_pct,
    };
  }, [pricing]);

  const materials = [
    { key: "all", label: "All" },
    { key: "nitrile", label: "Nitrile" },
    { key: "latex", label: "Latex" },
    { key: "vinyl", label: "Vinyl" },
    { key: "poly", label: "Poly" },
  ];

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-gloves-pricing">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Lock className="w-4 h-4 text-rose-600" />
            <p className="text-[11px] font-black uppercase tracking-[2px] text-rose-600">
              Admin Only · Internal Pricing
            </p>
          </div>
          <h1 className="text-2xl lg:text-3xl font-black text-slate-900">AMMEX Gloves — Cost &amp; Retail</h1>
          <p className="text-sm text-slate-600 mt-1">
            AMMEX B2B cost pricing (dated 8.4.2026) + our retail sell price. Never
            expose these figures on customer-facing pages.
          </p>
          {user?.email && (
            <p className="text-[11px] text-slate-500 mt-1">
              Viewing as <span className="font-bold">{user.email}</span> · role{" "}
              <span className="font-bold">{user.role}</span>
            </p>
          )}
        </div>
        {totals && (
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-black uppercase tracking-wider">
            <TrendingUp className="w-4 h-4" /> +{totals.markupPct}% markup rule
          </div>
        )}
      </div>

      {/* TOTALS STRIP */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Product lines" value={String(totals.lines)} />
          <StatCard label="Sum of cost / case" value={fmt(totals.totalCost)} tone="cost" />
          <StatCard label="Sum of retail / case" value={fmt(totals.totalRetail)} tone="retail" />
          <StatCard
            label="Sum of margin / case"
            value={fmt(totals.totalMargin)}
            tone="margin"
          />
        </div>
      )}

      {/* MATERIAL FILTER */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-[10px] font-black uppercase tracking-[1.5px] text-slate-500 mr-1">
          Filter:
        </span>
        {materials.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => setFilter(m.key)}
            data-testid={`admin-gloves-filter-${m.key}`}
            className={`px-3 py-1.5 rounded-md text-xs font-bold border transition-colors ${
              filter === m.key
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* STATES */}
      {loading && (
        <div className="p-8 text-center text-sm text-slate-500 border rounded-lg bg-white">
          Loading cost pricing…
        </div>
      )}
      {error && !loading && (
        <div className="p-6 rounded-lg border border-rose-200 bg-rose-50 text-rose-900 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-black text-sm mb-1">Cost pricing unavailable</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* TABLE */}
      {!loading && !error && (
        <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-white text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-black">Product</th>
                  <th className="text-left px-4 py-3 font-black">Brand</th>
                  <th className="text-left px-4 py-3 font-black">Material</th>
                  <th className="text-right px-4 py-3 font-black">Cost / case</th>
                  <th className="text-right px-4 py-3 font-black">Cost / pc</th>
                  <th className="text-right px-4 py-3 font-black">Retail / case</th>
                  <th className="text-right px-4 py-3 font-black">Retail / pc</th>
                  <th className="text-right px-4 py-3 font-black">Margin</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ product, cost }, i) => (
                  <tr
                    key={product.id}
                    className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}
                    data-testid={`admin-gloves-row-${product.id}`}
                  >
                    <td className="px-4 py-3">
                      <p className="font-bold text-slate-900 leading-tight">{product.name}</p>
                      <p className="text-[11px] text-slate-500">{product.variants.length} sizes · {product.mil} mil</p>
                    </td>
                    <td className="px-4 py-3 text-slate-700 font-semibold">{product.brand}</td>
                    <td className="px-4 py-3 text-slate-700 uppercase text-xs font-bold">{product.material}</td>
                    <td className="px-4 py-3 text-right font-black text-slate-800">
                      {fmt(cost?.cost_per_case)}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">
                      {fmtPiece(cost?.cost_per_piece)}
                    </td>
                    <td className="px-4 py-3 text-right font-black text-blue-700">
                      {fmt(cost?.retail_per_case ?? product.retailPerCase)}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">
                      {fmtPiece(cost?.retail_per_piece ?? product.retailPerPiece)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-xs font-black">
                        {fmt(cost?.margin_per_case)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-[11px] text-slate-500 mt-4 flex items-center gap-1.5">
        <Package className="w-3 h-3" />
        Cost data source: AMMEX B2BPriceListPDF 8.4.2026. Retail = Cost × 1.44.
      </p>

      {/* ANSELL SECTION */}
      {!loading && !error && ansellProducts.length > 0 && (
        <div className="mt-14" data-testid="admin-ansell-section">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[2px] text-rose-600 mb-1">Admin Only</p>
              <h2 className="text-xl font-black text-slate-900">Ansell Gloves — Cost &amp; Retail</h2>
              <p className="text-sm text-slate-600">
                {ansellProducts.length} product families · sourced from Sterile Mate Pricelist XLSX
              </p>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className="overflow-x-auto max-h-[600px]">
              <table className="w-full text-sm">
                <thead className="bg-slate-900 text-white text-xs uppercase tracking-wider sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-3 font-black">ProdFam</th>
                    <th className="text-left px-4 py-3 font-black">Brand</th>
                    <th className="text-left px-4 py-3 font-black">Product</th>
                    <th className="text-center px-4 py-3 font-black">UOM</th>
                    <th className="text-right px-4 py-3 font-black">Cost</th>
                    <th className="text-right px-4 py-3 font-black">Retail</th>
                    <th className="text-right px-4 py-3 font-black">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {ansellProducts.map((p, i) => {
                    const c = ansellCost[p.id];
                    return (
                      <tr key={p.id} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"} data-testid={`admin-ansell-row-${p.id}`}>
                        <td className="px-4 py-2 font-mono text-xs text-slate-700">{p.prodfam}</td>
                        <td className="px-4 py-2 text-xs font-bold text-slate-800 uppercase">{p.brand}</td>
                        <td className="px-4 py-2 text-xs text-slate-800">
                          <p className="font-semibold">{p.name}</p>
                          <p className="text-[10px] text-slate-500">{p.variants.length} sizes</p>
                        </td>
                        <td className="px-4 py-2 text-center text-xs font-bold">{p.uom}</td>
                        <td className="px-4 py-2 text-right text-xs font-black text-slate-800">{fmt(c?.cost)}</td>
                        <td className="px-4 py-2 text-right text-xs font-black text-blue-700">{fmt(c?.retail ?? p.retail)}</td>
                        <td className="px-4 py-2 text-right">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black ${
                            (c?.margin ?? 0) > 0 ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"
                          }`}>
                            {fmt(c?.margin)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 mt-3">
            Note: Ansell rows show <strong>cost == retail</strong> because the customer XLSX has the
            same value in both Price columns. Enter real markups via Refresh script or admin editor once available.
          </p>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const toneCls = {
    cost: "border-slate-300",
    retail: "border-blue-300 bg-blue-50",
    margin: "border-emerald-300 bg-emerald-50",
  }[tone] || "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border ${toneCls} px-4 py-3`}>
      <p className="text-[10px] font-black uppercase tracking-[1.5px] text-slate-500">{label}</p>
      <p className="text-xl font-black text-slate-900 mt-0.5">{value}</p>
    </div>
  );
}
